import { config } from "@/lib/shared/config";
import { path } from "../path/path";
import { expectShape } from "../expect/expect-shape";
import { is } from "../is/is";
import { POLL_SECONDS, PAGE_SIZE } from "@/lib/follow/follow-constants";
import type { SessionDetail } from "@/lib/session/session-detail";
import { consumeSessionEvents } from "@/lib/session/consume-session-events";
import type { ApiOutputs } from "@/lib/api/api-outputs";
import type { FollowRetryState } from "@/lib/session/retry/follow-retry-state";
import type { FollowSessionDependencies } from "@/lib/session/follow-session-dependencies";
import { defaultFollowSessionDependencies } from "@/lib/session/default-follow-session-dependencies";
import { requestWithFollowRetry } from "@/lib/session/retry/request-with-follow-retry";
import { isFollowStopStatus } from "@/lib/session/is-follow-stop-status";
import { isFollowStopSession } from "@/lib/session/is-follow-stop-session";
import { resolveFollowOutageBudget } from "@/lib/session/retry/resolve-follow-outage-budget";
import { followRequestTimeoutMs } from "@/lib/session/retry/follow-request-timeout-ms";

/**
 * Follow a session until it finishes or needs a person.
 *
 * Stopping on `waiting_human` / `awaiting_approval` is the important part: the
 * agent parks there and burns nothing, so a follower that kept waiting would
 * hang forever. The caller turns that into a distinct exit code, which is what
 * lets an orchestrating agent branch on "it asked me something".
 */
export async function followSession(
  sessionId: string,
  options: { quiet?: boolean; after?: number } = {},
  dependencies: FollowSessionDependencies = defaultFollowSessionDependencies,
): Promise<SessionDetail> {
  // Resuming a follow starts where the caller left off, so `--after` does not
  // replay (and reprint) the whole transcript.
  let cursor = options.after ?? 0;
  let emptyPolls = 0;
  const retry: FollowRetryState = {
    failures: 0,
    warned: false,
    lastWarnedAt: null,
    outageStartedAt: null,
  };
  const maxOutageMs = resolveFollowOutageBudget();
  // The transcript prints durable download links, which need the API origin.
  const { baseUrl } = config.load();

  for (;;) {
    const page = await requestWithFollowRetry({
      retry,
      maxOutageMs,
      dependencies,
      operation: async () =>
        expectShape<
          Omit<ApiOutputs["sessions"]["events"], "status"> & { status?: string }
        >(
          await dependencies.request(path`/sessions/${sessionId}/events`, {
            query: { after: cursor || undefined, limit: PAGE_SIZE },
            timeoutMs: followRequestTimeoutMs(
              retry,
              maxOutageMs,
              dependencies.now(),
            ),
          }),
          "GET /sessions/{id}/events",
          {
            items: is.array,
            hasMore: (value) => typeof value === "boolean",
            nextCursor: (value) => value === null || is.number(value),
            // Optional: a deployment older than this binary omits it, and a
            // missing field here would be a non-retryable schema mismatch.
            status: is.optionalString,
          },
        ),
    });

    cursor = consumeSessionEvents(page.items, cursor, options.quiet, baseUrl);

    // Advance on the SERVER's cursor, not on what we could render. The API
    // paginates raw rows and drops the ones with no transcript representation,
    // so a page can come back empty with hasMore true; following the items
    // alone would re-request the same page forever, without even sleeping.
    if (page.nextCursor !== null) {
      cursor = Math.max(cursor, page.nextCursor);
    }

    if (page.hasMore) {
      continue;
    }

    emptyPolls = page.items.length === 0 ? emptyPolls + 1 : 0;
    if (
      isFollowStopStatus(page.status) ||
      page.items.length > 0 ||
      emptyPolls >= 3
    ) {
      const session = await requestWithFollowRetry({
        retry,
        maxOutageMs,
        dependencies,
        operation: async () =>
          expectShape<SessionDetail>(
            await dependencies.request(path`/sessions/${sessionId}`, {
              timeoutMs: followRequestTimeoutMs(
                retry,
                maxOutageMs,
                dependencies.now(),
              ),
            }),
            "GET /sessions/{id}",
            { status: is.string },
          ),
      });
      emptyPolls = 0;
      if (isFollowStopSession(session)) {
        return session;
      }
    }

    await dependencies.pollSleep(POLL_SECONDS);
  }
}
